import {
  ArrowRightStartOnRectangleIcon,
  BellAlertIcon,
  BellIcon,
  BookOpenIcon,
  CalendarIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  IdentificationIcon,
  NewspaperIcon,
  PencilSquareIcon,
  ShoppingBagIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import AppPromoCard from '@/components/Nav/AppPromoCard';
import { RotatingPromo } from '@/components/Nav/RotatingPromo';
import Avatar from '@/components/UI/Avatar';
import { isStaffRole } from '@/renderer/utils/isStaffRole';
import useMe from '@/renderer/utils/useMe';

/**
 * The contents of the phone "More" sheet (#2142): everything the bottom tab
 * bar's four destinations don't cover, in the app's light (and dark) theme.
 *
 * Staff and admins get Admin as their third tab instead of Order, so for them
 * Order moves in here and the Admin row (now a tab) drops out of the Staff
 * section. Members keep Order as a tab.
 *
 * Public: signed-out visitors browsing /archive, /walls or the schedule on a
 * phone also have a More tab, so it shows sign-in and the public links to them.
 */
export function MoreMenu() {
  const user = useMe();
  const signedIn = !!user?.role;
  const isStaff = isStaffRole(user?.role);
  const isAuthor = user?.role === 'author' || user?.role === 'admin';

  return (
    <div className="space-y-5">
      {signedIn ? (
        <a
          href="/settings"
          className="flex items-center gap-3 rounded-2xl border border-zinc-950/10 bg-white px-4 py-3 text-inherit no-underline shadow-xs active:bg-zinc-100 dark:border-white/10 dark:bg-zinc-900 dark:active:bg-zinc-800"
        >
          <Avatar user={user} isAdmin={isStaff} />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold text-sm text-zinc-900 dark:text-zinc-100">
              {user.firstName} {user.lastName}
            </div>
            <div className="truncate text-xs text-zinc-500 dark:text-zinc-400">{user.email}</div>
          </div>
          <ChevronRightIcon className="size-4 shrink-0 text-zinc-400" aria-hidden="true" />
        </a>
      ) : (
        <Section>
          <Row href="/signin" icon={ArrowRightStartOnRectangleIcon} label="Sign in" />
          <Row href="/register" icon={UserPlusIcon} label="Create an account" />
        </Section>
      )}

      {/* Push adoption depends on members finding the app, so the card sits
          near the top. It renders nothing inside the native app. */}
      <AppPromoCard variant="page" />

      {signedIn && (
        <Section title="Account">
          <Row href="/settings" icon={Cog6ToothIcon} label="Account & settings" />
          <Row href="/settings/memberships" icon={CreditCardIcon} label="Memberships" />
          {/* Settings deep-links by ?scrollTo=<row id>; Telegram is the first
              alert row in the Notifications section. */}
          <Row href="/settings?scrollTo=telegram" icon={BellIcon} label="Notifications" />
          {/* Concierge chat beta (#2188): rows only for an enabled partner / for
              concierge staff once the beta is in use; nothing for anyone else. */}
          <ClientOnly component={() => import('@/components/ConciergeChat/ConciergeMoreRows')} />
        </Section>
      )}

      {signedIn && (
        <Section
          title="Active services"
          action={
            <a
              href="/settings/memberships"
              className="font-medium text-emerald-700 text-xs no-underline dark:text-emerald-400"
            >
              Manage
            </a>
          }
        >
          <ErrorBoundary>
            <ClientOnly component={() => import('@/components/Nav/ActiveServices')} variant="page">
              <div className="h-16 animate-pulse" />
            </ClientOnly>
          </ErrorBoundary>
        </Section>
      )}

      {isAuthor && (
        <Section title="Staff">
          <Row href="/editor" icon={PencilSquareIcon} label="Editor" />
        </Section>
      )}

      <Section title="Plays & guides">
        {/* Admin is a staff member's third tab, so Order lives here for them.
            /order branches to In-App Purchase on iOS itself. */}
        {isStaff && <Row href="/order" icon={ShoppingBagIcon} label="Order" />}
        <Row href="/release-schedule" icon={CalendarIcon} label="Release schedule" />
        <Row href="/walls" icon={NewspaperIcon} label="Walls' Blog" />
        <Row href="/welcome-guide" icon={BookOpenIcon} label="Welcome guide" />
        <Row href="/alerts-guide" icon={BellAlertIcon} label="Alert guide" />
        <Row href="/about" icon={IdentificationIcon} label="About RAS" />
      </Section>

      {signedIn && (
        <Section>
          <Row href="/signout" icon={ArrowRightStartOnRectangleIcon} label="Sign out" destructive />
        </Section>
      )}

      {/* Web and Android only: RotatingPromo never renders in the iOS app. */}
      <RotatingPromo tone="page" className="justify-center" />

      <div className="flex justify-center gap-4 text-xs">
        <a href="/privacy" className="text-zinc-500 no-underline dark:text-zinc-400">
          Privacy Policy
        </a>
        <a href="/terms" className="text-zinc-500 no-underline dark:text-zinc-400">
          Terms & Conditions
        </a>
      </div>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      {(title || action) && (
        <div className="flex items-baseline justify-between px-1 pb-1.5">
          {title && (
            <h2 className="m-0 font-semibold text-xs text-zinc-500 uppercase tracking-wider dark:text-zinc-400">
              {title}
            </h2>
          )}
          {action}
        </div>
      )}
      <div className="divide-y divide-zinc-900/5 overflow-hidden rounded-2xl border border-zinc-950/10 bg-white shadow-xs dark:divide-white/5 dark:border-white/10 dark:bg-zinc-900">
        {children}
      </div>
    </section>
  );
}

function Row({
  href,
  icon: Icon,
  label,
  destructive = false,
}: {
  href: string;
  icon: typeof Cog6ToothIcon;
  label: string;
  destructive?: boolean;
}) {
  return (
    // min-h-12 keeps every row a comfortable tap target. Press feedback is
    // `active:` only; `hover:` never matches on the touch devices this is for.
    <a
      href={href}
      className={twMerge(
        'flex min-h-12 items-center gap-3 px-4 py-3 text-sm text-zinc-900 no-underline active:bg-zinc-100 dark:text-zinc-100 dark:active:bg-zinc-800',
        destructive && 'text-red-600 dark:text-red-400',
      )}
    >
      <Icon
        className={twMerge(
          'size-5 shrink-0 text-zinc-500 dark:text-zinc-400',
          destructive && 'text-red-600 dark:text-red-400',
        )}
        aria-hidden="true"
      />
      <span className="flex-1">{label}</span>
      {!destructive && (
        <ChevronRightIcon className="size-4 shrink-0 text-zinc-400" aria-hidden="true" />
      )}
    </a>
  );
}
